import React, { useRef, useEffect, useMemo, useCallback } from 'react';
import { Scatter } from 'react-chartjs-2';
import { Chart as ChartJS, LinearScale, PointElement, Tooltip, Title, LineElement } from 'chart.js';
import type { ChartData, ChartOptions } from 'chart.js';
import { useSetAtom, useAtom, useStore } from 'jotai';
import type { GeoJsonProperties, MultiPolygon, Polygon } from 'geojson';
import { bbox, featureCollection } from '@turf/turf';
import { selectedDateAtom, exportChartCanvasAtom } from '../map/atoms';
import { cameraFlightAtom } from '../map/flyToFeature';
import { selectedFeatureAtom } from '../Incident/FeaturePopup';
import { HISTORY_HALF_WINDOW_MINUTES } from '../map/layers/WeatherEventsLayer';
import { fetchFlatGeobuf, type GeometryVariant } from '../services/featuresApi';
import { NIFC_BOUNDS_GEOMETRY_LADDER } from '../map/geometryDetail';
import {
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  Box,
  CircularProgress,
} from '@mui/material';
import {
  DETAIL_ID_FIELD,
  DETAIL_TIMESTAMP_FIELD,
  eventIdentity,
  fetchWeatherEventDetails,
  THREAT_HISTORY_TABLE,
  type ChartPoint,
  type WeatherEventDetail,
} from '../services/weatherHistory';
import type { MapView } from '../services/types';

ChartJS.register(LinearScale, PointElement, Tooltip, Title, LineElement);

type Props = {
  events: ChartPoint[] | null;
  loading?: boolean;
  selectedMap: MapView;
};

const SIXTY_DAYS = 60;
const ONE_DAY_MS = 24 * 60 * 60 * 1000;

// The perimeter table also serves a generalized geometry, and framing a
// perimeter's bounds does not need it at full resolution. whole[0] is the
// coarsest whole-table rung of the NIFC ladder.
const EVENT_GEOMETRY_VARIANT: Partial<Record<MapView, GeometryVariant>> = {
  nifc: NIFC_BOUNDS_GEOMETRY_LADDER.whole[0].variant,
};

/** Whether a feature's properties carry the event identity, column for column. */
function hasIdentity(
  properties: GeoJsonProperties,
  identity: Record<string, string | null>
): boolean {
  return Object.entries(identity).every(([field, value]) =>
    value == null ? properties?.[field] == null : String(properties?.[field]) === value
  );
}

const SCATTER_POINT_COLOR: Record<MapView, string> = {
  wow: 'darkgreen',
  nifc: 'darkorange',
  canopy: 'darkorange',
  // The zero-recovery end of the Ting ramp.
  ting: 'rgb(214, 40, 40)',
};

// Chart.js caps a vertical axis at half the chart width, so long category names
// (e.g. "Last 24 hours Quantitative Precipitation Estimate") get clipped on the
// left. Word-wrap them onto multiple lines that each stay within that cap.
const MAX_LABEL_LINE_CHARS = 28;

export function wrapLabel(label: string): string | string[] {
  if (label.length <= MAX_LABEL_LINE_CHARS) {
    return label;
  }
  const lines: string[] = [];
  let current = '';
  for (const word of label.split(' ')) {
    const candidate = current ? `${current} ${word}` : word;
    if (current && candidate.length > MAX_LABEL_LINE_CHARS) {
      lines.push(current);
      current = word;
    } else {
      current = candidate;
    }
  }
  if (current) {
    lines.push(current);
  }
  return lines;
}

type ScatterDataPoint = {
  x: number;
  y: number;
  yGroup: string;
  point: ChartPoint;
};

export const WeatherEventScatter: React.FC<Props> = ({ events, loading = false, selectedMap }) => {
  const scatterWrapperRef = useRef<HTMLDivElement>(null);
  const setExportChartCanvas = useSetAtom(exportChartCanvasAtom);

  const [, setSelectedDate] = useAtom(selectedDateAtom);
  const flyTo = useSetAtom(cameraFlightAtom);
  const store = useStore();
  const [menuPosition, setMenuPosition] = React.useState<{
    x: number;
    y: number;
  } | null>(null);
  const [detailedData, setDetailedData] = React.useState<{ attributes: WeatherEventDetail }[]>([]);
  const [isLoadingDetails, setIsLoadingDetails] = React.useState(false);
  const [detailsFailed, setDetailsFailed] = React.useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  // Monotonic id so a slow detail fetch from an earlier click cannot overwrite
  // the results of a later one.
  const detailRequestRef = useRef(0);
  // Same guard for the geometry fetch behind a dropdown selection: two quick
  // picks must leave the camera on the later one, whichever response lands first.
  const geometryRequestRef = useRef(0);

  // Unique Y groups (category rows), mapped to integer Y coordinates.
  const yGroups = useMemo(() => {
    const groups = Array.from(new Set((events ?? []).map(e => e.yGroup))).sort();
    return groups;
  }, [events]);

  const yGroupMap = useMemo(() => {
    return Object.fromEntries(yGroups.map((g, i) => [g, i]));
  }, [yGroups]);

  const processedData = useMemo<ScatterDataPoint[]>(() => {
    return (events ?? []).map(point => ({
      x: point.timestamp,
      y: yGroupMap[point.yGroup] ?? 0,
      yGroup: point.yGroup,
      point,
    }));
  }, [events, yGroupMap]);

  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      const canvas = scatterWrapperRef.current?.querySelector('canvas') ?? null;
      setExportChartCanvas(prev => ({ ...prev, scatter: canvas }));
    });
    return () => {
      cancelAnimationFrame(frame);
      setExportChartCanvas(prev => ({ ...prev, scatter: null }));
    };
  }, [setExportChartCanvas, processedData]);

  // Close menu when clicking outside
  useEffect(() => {
    if (!menuPosition) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setMenuPosition(null);
        setDetailedData([]);
        setIsLoadingDetails(false);
        setDetailsFailed(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuPosition]);

  // Lazily fetch the underlying records for the clicked point(s). The chart only
  // holds aggregated buckets, so detail rows are loaded on demand per click.
  const handlePointClick = useCallback(
    async (points: ChartPoint[], event: React.MouseEvent<HTMLElement>) => {
      const requestId = ++detailRequestRef.current;
      setMenuPosition({ x: event.clientX, y: event.clientY });
      setIsLoadingDetails(true);
      setDetailsFailed(false);
      setDetailedData([]);
      try {
        const rows = await fetchWeatherEventDetails(selectedMap, points);
        if (detailRequestRef.current !== requestId) return;
        setDetailedData(rows.map(attributes => ({ attributes })));
      } catch (error) {
        if (detailRequestRef.current !== requestId) return;
        console.error('Error fetching event details:', error);
        setDetailedData([]);
        setDetailsFailed(true);
      } finally {
        if (detailRequestRef.current === requestId) setIsLoadingDetails(false);
      }
    },
    [selectedMap]
  );

  const handleMenuItemClick = useCallback(
    (detailItem: { attributes: WeatherEventDetail }) => {
      const raw = detailItem.attributes[DETAIL_TIMESTAMP_FIELD[selectedMap]];
      const timestamp = raw != null ? new Date(raw).getTime() : 0;

      if (timestamp > 0) {
        setSelectedDate(new Date(timestamp));
      }

      // The detail rows carry attributes only, so the event's polygons are
      // fetched from its history table (same snapshot window the historical
      // layer renders) to zoom the map to the clicked event. The features API
      // takes one equality filter, so the request filters on the most selective
      // identity column it has a value for and the rest of the identity is
      // matched on the returned rows, which carry every identity column.
      const identity = eventIdentity(selectedMap, detailItem.attributes);
      const filterField = Object.keys(identity).find(field => identity[field] != null);
      const historyTable = THREAT_HISTORY_TABLE[selectedMap];
      if (timestamp > 0 && filterField != null) {
        const requestId = ++geometryRequestRef.current;
        const halfWindowMs = HISTORY_HALF_WINDOW_MINUTES[selectedMap] * 60 * 1000;
        fetchFlatGeobuf<Polygon | MultiPolygon, WeatherEventDetail>(historyTable, {
          dateField: DETAIL_TIMESTAMP_FIELD[selectedMap],
          from: new Date(timestamp - halfWindowMs),
          to: new Date(timestamp + halfWindowMs),
          filterField,
          filterValue: String(identity[filterField]),
          fields: Object.keys(identity),
          geometry: EVENT_GEOMETRY_VARIANT[selectedMap],
        })
          .catch((error: unknown) => {
            // Only the fetch is caught: a failure in the framing below is a
            // camera bug and must surface as itself, not as a fetch error.
            if (geometryRequestRef.current === requestId) {
              console.error('Error fetching event geometry:', error);
            }
            return null;
          })
          .then(eventGeometry => {
            if (eventGeometry == null || geometryRequestRef.current !== requestId) return;
            const eventFeatures = eventGeometry.features.filter(feature =>
              hasIdentity(feature.properties, identity)
            );
            if (eventFeatures.length > 0) {
              // The popup describing this very event stays open, since the
              // flight only frames what it already shows. Read as the flight
              // starts, not as the row was clicked: a map click during the
              // fetch may have opened a popup for another feature.
              const popupFeature = store.get(selectedFeatureAtom);
              flyTo({
                bounds: bbox(featureCollection(eventFeatures)),
                keepPopup: popupFeature != null && hasIdentity(popupFeature.properties, identity),
              });
            }
          });
      }
      setMenuPosition(null);
      setDetailedData([]);
      setIsLoadingDetails(false);
      setDetailsFailed(false);
    },
    [selectedMap, setSelectedDate, flyTo, store]
  );

  const datasets: ChartData<'scatter'>['datasets'] = useMemo(
    () => [
      {
        label: 'Weather Events',
        data: processedData,
        backgroundColor: SCATTER_POINT_COLOR[selectedMap],
        pointRadius: 2.5,
        pointBorderWidth: 0.5,
        pointBorderColor: 'rgba(255, 255, 255, 0.3)',
      },
    ],
    [processedData, selectedMap]
  );

  const data: ChartData<'scatter'> = useMemo(
    () => ({
      datasets,
    }),
    [datasets]
  );

  const options: ChartOptions<'scatter'> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      onClick: (_event, elements, chart) => {
        if (elements && elements.length > 0) {
          const points = elements.map(
            element =>
              (chart.data.datasets[element.datasetIndex].data[element.index] as ScatterDataPoint)
                .point
          );
          handlePointClick(points, _event.native as unknown as React.MouseEvent<HTMLElement>);
        }
      },
      // Pad the canvas so y-axis labels aren't flush against the panel's left
      // edge and edge points aren't clipped by the canvas border.
      layout: {
        padding: { left: 12, right: 10, top: 8, bottom: 6 },
      },
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          enabled: false,
        },
      },
      scales: {
        x: {
          type: 'linear',
          title: {
            display: false,
            text: 'Time',
            color: '#ffffff',
          },
          // One extra day of range on each side keeps points at "now" and the
          // 60-day mark off the plot-area edges so their symbols aren't clipped.
          min:
            new Date(new Date().setDate(new Date().getDate() - SIXTY_DAYS)).getTime() - ONE_DAY_MS,
          max: new Date().getTime() + ONE_DAY_MS,
          ticks: {
            color: '#ffffff',
            callback: (value: string | number) => {
              return new Date(value).toLocaleString('en-US', {
                month: 'short',
                day: 'numeric',
              });
            },
            maxTicksLimit: 12,
          },
          grid: {
            color: 'rgba(255, 255, 255, 0.1)',
          },
        },
        y: {
          type: 'linear',
          title: {
            display: false,
            text: 'Group',
            color: '#ffffff',
          },
          // Bound the axis to exactly one slot per category with half a slot of
          // padding top and bottom so edge-row points aren't clipped. bounds:
          // 'data' keeps these exact (Chart.js would otherwise snap min/max to
          // generated ticks).
          min: -0.5,
          max: yGroups.length - 0.5,
          bounds: 'data',
          // Force one tick per category at its integer slot. Letting Chart.js
          // generate ticks across a [-0.5, n-0.5] range lands them on the
          // half-integer slot boundaries, where the label callback renders blank.
          afterBuildTicks: axis => {
            axis.ticks = yGroups.map((_, i) => ({ value: i }));
          },
          ticks: {
            color: '#ffffff',
            callback: (value: string | number) => {
              const group = yGroups[Number(value)];
              return group ? wrapLabel(group) : '';
            },
            autoSkip: false,
            font: {
              size: 12,
            },
          },
          grid: {
            color: 'rgba(255, 255, 255, 0.1)',
          },
        },
      },
    }),
    [yGroups, handlePointClick]
  );

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          p: 2,
        }}
      >
        <CircularProgress size={40} sx={{ color: 'white' }} />
      </Box>
    );
  }

  if (!events || events.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          p: 2,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: 'white',
            textAlign: 'center',
          }}
        >
          No weather events available
          <br />
          Selected Map: {selectedMap}
        </Typography>
      </Box>
    );
  }

  return (
    <>
      {/* Absolutely fill the (position: relative) chart area. The canvas gets an
          inline pixel size from Chart.js each layout; kept in-flow, that size
          becomes the flex chain's content minimum, so the chart could grow with
          the panel but never shrink back. Absolute positioning takes the canvas
          out of that equation entirely. */}
      <div ref={scatterWrapperRef} style={{ position: 'absolute', inset: 0 }}>
        <Scatter data={data} options={options} />
      </div>
      {/* Rendered for every state a click can land in -- loading, rows, a
          failed fetch, and no matching records -- so a click never silently
          does nothing and a failure is never mistaken for "no events". */}
      {menuPosition && (
        <Box
          ref={dropdownRef}
          sx={{
            position: 'absolute',
            top: 8,
            left: 8,
            zIndex: 1300,
          }}
        >
          <List
            sx={{
              bgcolor: 'background.paper',
              boxShadow: 3,
              borderRadius: 1,
              maxHeight: 400,
              width: 350,
              overflow: 'auto',
            }}
          >
            {isLoadingDetails ? (
              <ListItem sx={{ justifyContent: 'center', gap: 1, py: 2 }}>
                <CircularProgress size={20} />
                <ListItemText
                  sx={{ flex: 'none' }}
                  primary={
                    <Typography variant="body2" color="primary">
                      Loading detailed data...
                    </Typography>
                  }
                />
              </ListItem>
            ) : detailsFailed ? (
              <ListItem sx={{ py: 2 }}>
                <ListItemText
                  primary={
                    <Typography variant="body2" color="error">
                      Event details failed to load
                    </Typography>
                  }
                />
              </ListItem>
            ) : detailedData.length === 0 ? (
              <ListItem sx={{ py: 2 }}>
                <ListItemText
                  primary={
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.secondary',
                      }}
                    >
                      No detail records for this point
                    </Typography>
                  }
                />
              </ListItem>
            ) : (
              detailedData.map((detailItem, index) => {
                const raw = detailItem.attributes[DETAIL_TIMESTAMP_FIELD[selectedMap]];
                const timestamp = raw != null ? new Date(raw).getTime() : 0;

                const idValue = detailItem.attributes[DETAIL_ID_FIELD[selectedMap]];

                return (
                  <ListItem
                    // A single threat_id spans multiple detail rows (e.g. per
                    // county), so the index keeps the list key unique.
                    key={`${idValue ?? 'item'}-${index}`}
                    sx={{
                      borderBottom: index < detailedData.length - 1 ? '1px solid #eee' : 'none',
                    }}
                  >
                    <ListItemButton
                      onClick={() => handleMenuItemClick(detailItem)}
                      sx={{
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        },
                      }}
                    >
                      <ListItemText
                        primary={
                          <>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                              {(selectedMap === 'canopy' && detailItem.attributes.event_name) ||
                                (selectedMap === 'wow' && detailItem.attributes.category) ||
                                (selectedMap === 'nifc' && detailItem.attributes.incidentname) ||
                                (selectedMap === 'ting' && detailItem.attributes.power_state) ||
                                'Unknown Event'}
                            </Typography>
                            <Typography variant="body2" color="primary">
                              {(selectedMap === 'canopy' && detailItem.attributes.kml_id) ||
                                // A forecast product has no threat_id; its
                                // datasource is the product's name.
                                (selectedMap === 'wow' &&
                                  (detailItem.attributes.threat_id ||
                                    detailItem.attributes.datasource)) ||
                                (selectedMap === 'nifc' && detailItem.attributes.incidentname) ||
                                (selectedMap === 'ting' && detailItem.attributes.event_id) ||
                                'Unknown Group'}
                            </Typography>
                            {selectedMap === 'canopy' && (
                              <>
                                <Typography variant="body2">
                                  {detailItem.attributes.county_intersect ?? 'Unknown County'}
                                  {' - '}
                                  {detailItem.attributes.state_intersect ?? 'Unknown State'}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: 'text.secondary',
                                  }}
                                >
                                  Structures Exposed:{' '}
                                  {detailItem.attributes.structures_exposed ?? 0}
                                </Typography>
                                <br />
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: 'text.secondary',
                                  }}
                                >
                                  Residential:{' '}
                                  {detailItem.attributes.residential_struc_exposed ?? 0}
                                </Typography>
                              </>
                            )}
                            {selectedMap === 'wow' && (
                              <>
                                <Typography variant="body2">
                                  {detailItem.attributes.county_intersect ?? 'Unknown County'}
                                  {' - '}
                                  {detailItem.attributes.state_intersect ?? 'Unknown State'}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: 'text.secondary',
                                  }}
                                >
                                  Threat: {detailItem.attributes.threat ?? 'Unknown'}
                                </Typography>
                              </>
                            )}
                            {selectedMap === 'nifc' && (
                              <Typography variant="body2">
                                Incident: {detailItem.attributes.incidentname ?? 'Unknown'}
                              </Typography>
                            )}
                            {selectedMap === 'ting' && (
                              <>
                                <Typography variant="body2">
                                  {detailItem.attributes.county_intersect ?? 'Unknown County'}
                                  {' - '}
                                  {detailItem.attributes.state_intersect ?? 'Unknown State'}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: 'text.secondary',
                                  }}
                                >
                                  Structures Exposed:{' '}
                                  {detailItem.attributes.structures_exposed ?? 0}
                                </Typography>
                                <br />
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: 'text.secondary',
                                  }}
                                >
                                  Residential:{' '}
                                  {detailItem.attributes.residential_struc_exposed ?? 0}
                                </Typography>
                                <br />
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: 'text.secondary',
                                  }}
                                >
                                  Restored:{' '}
                                  {typeof detailItem.attributes.recovered_percentage === 'number'
                                    ? `${Math.round(detailItem.attributes.recovered_percentage * 100)}%`
                                    : 'Unknown'}
                                </Typography>
                              </>
                            )}
                          </>
                        }
                        secondary={
                          <>
                            <Typography
                              variant="caption"
                              sx={{
                                color: 'text.secondary',
                              }}
                            >
                              {timestamp > 0
                                ? new Date(timestamp).toLocaleString()
                                : 'Unknown Date'}
                              {/* A row without an id (forecast products) has
                                  no ID line: "ID: Unknown" says nothing. */}
                              {idValue != null && (
                                <>
                                  <br />
                                  ID: {idValue}
                                </>
                              )}
                            </Typography>
                          </>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })
            )}
          </List>
        </Box>
      )}
    </>
  );
};
